/**
 * "Olvidé mi contraseña": pedir el enlace de recuperación.
 *
 * La regla que gobierna esta pantalla: **nunca dice si el correo existe.**
 * Responde lo mismo para una cuenta real que para una dirección inventada. Si
 * distinguiera, cualquiera podría usarla para averiguar quién tiene cuenta en
 * el panel de una redacción — y esa lista es el primer paso de un ataque
 * dirigido.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button, Card, ErrorMessage, Field, Input, Notice } from '../components/ui.tsx';
import { authClient } from '../lib/auth-client.ts';

export function ForgotPasswordPage() {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const { error: requestError } = await authClient.requestPasswordReset({
      email,
      // A dónde lleva el enlace del correo. Tiene que ser nuestro propio origen:
      // el servidor rechaza cualquier otro, para que nadie pueda fabricar un
      // correo legítimo nuestro que acabe en una página suya.
      redirectTo: `${window.location.origin}/nueva-contrasena`,
    });

    setLoading(false);

    // Solo se enseña error cuando el problema es NUESTRO o de la red —demasiados
    // intentos, servidor caído—. "Ese correo no existe" no es un error que esta
    // pantalla conozca.
    if (requestError && requestError.status !== 400 && requestError.status !== 404) {
      setError(requestError.status === 429 ? t('auth.forgot.tooMany') : t('auth.forgot.failed'));
      return;
    }

    setSent(true);
  }

  return (
    <AuthLayout>
      <Card title={t('auth.forgot.title')}>
        {sent ? (
          <>
            <Notice>{t('auth.forgot.sent', { email })}</Notice>
            <p className="text-sm leading-relaxed text-[var(--color-muted)]">{t('auth.forgot.sentHelp')}</p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="mb-5 text-sm leading-relaxed text-[var(--color-muted)]">{t('auth.forgot.intro')}</p>

            <ErrorMessage>{error}</ErrorMessage>

            <Field id="email" label={t('common.email')}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>

            <Button type="submit" loading={loading} disabled={email.trim() === ''}>
              {loading ? t('common.loading') : t('auth.forgot.submit')}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-sm">
          <Link to="/entrar" className="text-[var(--color-brand)] hover:underline">
            {t('auth.forgot.back')}
          </Link>
        </p>
      </Card>
    </AuthLayout>
  );
}
